import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';

/**
 * Promise-tabanlı onay (confirm) diyaloğu — `window.confirm()` yerine.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (!await confirm({ message: 'Silmek istediğine emin misin?', danger: true })) return;
 *
 * Seçenekler:
 *   - title         (opsiyonel) Başlık metni
 *   - message       (zorunlu)   Ana mesaj
 *   - confirmLabel  (opsiyonel) Onay butonu metni (default: t('confirmBtn'))
 *   - cancelLabel   (opsiyonel) İptal butonu metni (default: t('cancel'))
 *   - danger        (opsiyonel) true → kırmızı onay butonu (silme vb.)
 *
 * Davranış:
 *   - ESC tuşu, ✕ butonu, backdrop tıklaması → iptal (resolve false)
 *   - Onay → resolve true
 *   - Açılırken confirm butonuna odaklanır, kapanırken önceki fokus geri yüklenir
 *   - Mobilde bottom-sheet, desktopta ortalanmış kart
 *   - role="alertdialog", aria-modal="true", aria-labelledby / aria-describedby
 */

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { options, resolve } | null
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ options });
    });
  }, []);

  const close = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(result);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog
          options={state.options}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}

function ConfirmDialog({ options, onConfirm, onCancel }) {
  const { t } = useLanguage();
  const confirmBtnRef = useRef(null);
  const previousFocusRef = useRef(null);

  const {
    title,
    message,
    confirmLabel,
    cancelLabel,
    danger = false,
  } = options || {};

  // ESC tuşu ile iptal + focus yönetimi
  useEffect(() => {
    previousFocusRef.current = document.activeElement;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    // Fokusu onay butonuna ver (micro-delay: DOM boyama sonrası)
    const focusTimer = setTimeout(() => {
      confirmBtnRef.current?.focus();
    }, 0);

    // Body scroll lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      // Önceki odağı geri yükle
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        try { previousFocusRef.current.focus(); } catch (_) { /* noop */ }
      }
    };
  }, [onCancel]);

  const confirmClass = danger
    ? 'bg-red-600 hover:bg-red-700 active:bg-red-800 text-white focus:ring-red-400'
    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white focus:ring-blue-400';

  const resolvedTitle = title || t('confirmDefaultTitle') || 'Onay';
  const resolvedConfirm = confirmLabel || t('confirmBtn') || 'Onayla';
  const resolvedCancel = cancelLabel || t('cancel') || 'İptal';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className="relative bg-white dark:bg-gray-900 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 flex flex-col animate-confirm-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-4 pb-2 sm:pt-5">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                danger
                  ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
                  : 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
              }`}
              aria-hidden="true"
            >
              <span className="text-xl leading-none">{danger ? '⚠️' : '❓'}</span>
            </div>
            <h2
              id="confirm-dialog-title"
              className="text-lg font-bold text-gray-900 dark:text-white leading-tight"
            >
              {resolvedTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 -mt-1 -mr-1 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label={resolvedCancel}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        {/* Message */}
        <div className="px-5 pb-5 pt-1">
          <p
            id="confirm-dialog-message"
            className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line"
          >
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 pt-1 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 border-t border-gray-100 dark:border-gray-800 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2.5 rounded-xl font-semibold text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            {resolvedCancel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm active:scale-[0.98] transition-all focus:outline-none focus:ring-2 ${confirmClass}`}
          >
            {resolvedConfirm}
          </button>
        </div>
      </div>
    </div>
  );
}
